import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Select } from "../../components/shared/Select";
import { HStack, VStack, Error } from "../../components/utils";
import { useShareholderInstrumentStore } from "../../store/shareholderInstrumentStore";
import { Input, Label } from "../../components/shared/InputField";
import {
  SecondaryCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { ShowNumberInWords } from "../../components/shared/UiElement";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { useNewShareholderInstrumentStore } from "../../store/newShareholderAndInstrumentStore";
import { useGetSecondaryInvestors } from "../../queries/transactionRound";
import { EmployeeExerciseTransaction } from "./RoundTransactionModel";
import { useGetInstrumentAndShareHolder } from "../../queries/quickRound";
import { useAuthStore } from "../../store";

type AddEsopExerciseModalProps = {
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
  data: EmployeeExerciseTransaction;
  mode: string;
};

const AddESOPExercisePopup = ({
  onPrimaryAction = () => {},
  onSecondaryAction = () => {},
  data,
  mode,
}: AddEsopExerciseModalProps) => {
  const shareholderInstruments = useShareholderInstrumentStore();
  const [onHoverNumberOfShares, setOnHoverNumberOfShares] = useState(false);
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();

  const [shareHolders, setShareholders] = useState<
    { id: string; name: string }[]
  >([]);
  const [instruments, setInstruments] = useState<
    { id: string; name: string }[]
  >([]);

  const companyId = useAuthStore.getState().companyId || "";

  const { data: instrumentShareholderInfoData } =
    useGetInstrumentAndShareHolder(companyId);
  const shareholderAndInstrumentstore = useShareholderInstrumentStore();
  useEffect(() => {
    shareholderAndInstrumentstore.setInstrumentClass(
      instrumentShareholderInfoData?.instrumentClasses || []
    );
    shareholderAndInstrumentstore.setShareholders(
      instrumentShareholderInfoData?.shareholders || []
    );
    shareholderAndInstrumentstore.setInstrumentClassSubTypes(
      instrumentShareholderInfoData?.instrumentClassSubTypes || {}
    );
  }, []);

  useEffect(() => {
    setShareholders(
      shareholderInstruments?.shareholders.filter(
        (shareholder: any) => shareholder.name.toLowerCase() !== "esop pool"
      ) || []
    );
    setInstruments(
      shareholderInstruments?.instrumentClasses.filter(
        (instrument) => instrument.subType === "Equity"
      ) || []
    );
  }, [
    shareholderInstruments?.shareholders,
    shareholderInstruments?.instrumentClasses,
  ]);

  const initialShareHolderValues: EmployeeExerciseTransaction = {
    uuid: data?.uuid || "",
    employeeName: data?.employeeName || "",
    companyInvestorId: data?.companyInvestorId || "",
    isExistingShareholder: data?.isExistingShareholder || false,
    amount: data?.amount || 0,
    exercisePrice: data?.exercisePrice || 0,
    numberOfShares: data?.numberOfShares || 0,
    instrumentClassId: data?.instrumentClassId || "",
  };

  const esopExerciseTransactionInputSchema = Yup.object({
    employeeName: Yup.string().label("Employee Name"),
    companyInvestorId: Yup.string().label("Employee ID"),
    instrumentClassId: Yup.string().required().label("Security"),
    numberOfShares: Yup.number()
      .integer()
      .min(1)
      .required()
      .label("Number of Shares"),
    exercisePrice: Yup.number().min(0).required().label("Exercise Price"),
    isExistingShareholder: Yup.boolean()
      .required()
      .label("Is Existing Shareholder"),
  });

  const esopExerciseStore = useNewShareholderInstrumentStore();

  const handleSubmit = (values: EmployeeExerciseTransaction) => {
    const esopExercises = esopExerciseStore.esopExercises.filter(
      (esopExercise) => esopExercise.uuid !== values.uuid
    );

    // const exists = esopExercises.some(
    //   (transaction) =>
    //     transaction.employeeName === values.employeeName &&
    //     transaction.instrumentClassId === values.instrumentClassId &&
    //     transaction.companyInvestorId === values.companyInvestorId &&
    //     transaction.isExistingShareholder === values.isExistingShareholder
    // );

    // if (exists) {
    //   toast("Transaction already exists!", { type: "error", autoClose: 2000 });
    //   return;
    // }

    const instrument = shareholderInstruments.instrumentClasses.find(
      (instrument) => instrument.id === values.instrumentClassId
    );
    if (instrument) {
      if (instrument.parValue > values.exercisePrice) {
        toast(`Exercise Price cannot be less than Security PAR value`, {
          type: "error",
          autoClose: 2000,
        });
        return;
      }
    }

    const existingIndex = esopExerciseStore.esopExercises.findIndex(
      (transaction: any) => transaction.uuid === values.uuid
    );

    const esopExercise: EmployeeExerciseTransaction = {
      uuid: uuidv4(),
      employeeName: values?.employeeName || "",
      companyInvestorId: values?.companyInvestorId || "",
      isExistingShareholder: values?.isExistingShareholder || false,
      amount: (values?.numberOfShares || 0) * (values?.exercisePrice || 0),
      exercisePrice: values?.exercisePrice || 0,
      numberOfShares: values?.numberOfShares || 0,
      instrumentClassId: values?.instrumentClassId || "",
    };
    if (existingIndex !== -1) {
      esopExerciseStore.esopExercises[existingIndex] = esopExercise;
      esopExerciseStore.setEsopExercises([...esopExerciseStore.esopExercises]);
      toast("Transaction updated successfully", {
        type: "success",
        autoClose: 2000,
      });
    } else {
      esopExerciseStore.setEsopExercises([
        ...esopExerciseStore.esopExercises,
        esopExercise,
      ]);
      toast("Transaction added successfully", {
        type: "success",
        autoClose: 2000,
      });
    }
    onPrimaryAction();
  };

  return (
    <VStack>
      <HStack className="justify-between px-6 border-b py-4">
        <p className="text-base font-medium">{mode} ESOP Exercise</p>
        <XMarkIcon
          className="h-6 w-6 cursor-pointer"
          onClick={() => onSecondaryAction()}
        />
      </HStack>

      <div className="bg-white rounded-lg px-4 py-4">
        <VStack className="justify-between">
          <Formik
            key="addESOPExercise"
            enableReinitialize={true}
            initialValues={initialShareHolderValues}
            validationSchema={esopExerciseTransactionInputSchema}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
          >
            {(formik) => (
              <Form key="shareholder">
                <VStack className="w-full  px-2 py-4 gap-6">
                  <VStack className="gap-8">
                    <HStack className="gap-8  md:flex-row flex-col">
                      <div className="flex-1 w-72">
                        <Label className="text-sm font-normal">Employee</Label>
                        <Select
                          options={shareHolders}
                          valueGetter={(shareholder) => shareholder.id}
                          textGetter={(shareholder) => shareholder.name}
                          {...formik.getFieldProps("companyInvestorId")}
                          onChange={(selectedOption) => {
                            const selectedInvestor =
                              shareholderInstruments?.shareholders?.find(
                                (seller: any) =>
                                  seller.id === selectedOption.target.value
                              );
                            if (selectedInvestor) {
                              formik.setFieldValue(
                                "companyInvestorId",
                                selectedInvestor.id
                              );
                              formik.setFieldValue(
                                "isExistingShareholder",
                                true
                              );
                            }
                          }}
                        />
                        {formik.touched?.companyInvestorId &&
                          formik.errors?.companyInvestorId && (
                            <Error text={formik.errors?.companyInvestorId} />
                          )}
                      </div>
                      <div className="flex-1 w-72">
                        <Label className="text-sm font-normal">Security</Label>
                        <Select
                          options={instruments}
                          valueGetter={(instrument) => instrument.id}
                          textGetter={(instrument) => instrument.name}
                          {...formik.getFieldProps("instrumentClassId")}
                        />
                        {formik.touched?.instrumentClassId &&
                          formik.errors?.instrumentClassId && (
                            <Error text={formik.errors?.instrumentClassId} />
                          )}
                      </div>
                    </HStack>
                    <HStack className="w-full gap-8 mx-auto grid grid-cols-1 md:grid-cols-2">
                      <div className="flex-1 w-72">
                        <Label className="text-sm font-normal">
                          Number Of Shares
                        </Label>
                        <Input
                          type="text"
                          onMouseEnter={() => {
                            setOnHoverNumberOfShares(true);
                          }}
                          onMouseLeave={() => {
                            setOnHoverNumberOfShares(false);
                          }}
                          onChange={(e: any) => {
                            const values = parseInt(
                              e.target.value?.replaceAll(",", "") || 0,
                              10
                            );
                            formik.setFieldValue("numberOfShares", values);
                            formik.handleChange("numberOfShares");
                          }}
                          value={Intl.NumberFormat(currencyType).format(
                            formik.values.numberOfShares
                          )}
                        />
                        {onHoverNumberOfShares && (
                          <ShowNumberInWords
                            value={formik.values.numberOfShares}
                            width={38}
                            currency={currencyType}
                          />
                        )}

                        {formik.touched.numberOfShares &&
                          formik.errors?.numberOfShares && (
                            <Error text={formik.errors?.numberOfShares} />
                          )}
                      </div>
                      <div className="flex-1 w-72">
                        <Label className="text-sm font-normal">
                          Exercise Price ({currencySymbol})
                        </Label>
                        <Input
                          type="number"
                          {...formik.getFieldProps("exercisePrice")}
                        />
                        {formik.touched.exercisePrice &&
                          formik.errors?.exercisePrice && (
                            <Error text={formik.errors?.exercisePrice} />
                          )}
                      </div>
                    </HStack>
                  </VStack>

                  <HStack className="justify-between mt-10">
                    <SecondaryCTAButton
                      event-name="Cancel ESOP Exercise Modal"
                      onClick={() => onSecondaryAction()}
                      type="reset"
                      className="text-red-500"
                    >
                      Cancel
                    </SecondaryCTAButton>
                    <PrimaryCTAButton
                      event-name="Save ESOP Exercise Modal"
                      type="submit"
                    >
                      {mode}
                    </PrimaryCTAButton>
                  </HStack>
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      </div>
    </VStack>
  );
};

export default AddESOPExercisePopup;
